<template>
  <!-- 收款方-提现记录 -->
  <content-container :table-row-count="tableData.length">
    <search-container>
      <search-col>
        <el-input v-model="params.requestNo" placeholder="易宝订单编号" clearable @keydown.enter="doSearch" />
      </search-col>
      <search-col>
        <el-input v-model="params.orderNo" placeholder="平台订单编号" clearable @keydown.enter="doSearch" />
      </search-col>
      <search-col>
        <el-input v-model="params.accountNo" placeholder="收款方账号" clearable @keydown.enter="doSearch" />
      </search-col>

      <search-col>
        <el-select
          v-model="params.status"
          style="width: calc(100% - 5px)"
          placeholder="业务类型"
          clearable
        >
          <el-option :label="yibaoWithdrawalStatusLabel[EYibaoWithdrawalStatus.REQUEST_RECEIVE]" :value="EYibaoWithdrawalStatus.REQUEST_RECEIVE" />
          <el-option :label="yibaoWithdrawalStatusLabel[EYibaoWithdrawalStatus.REQUEST_ACCEPT]" :value="EYibaoWithdrawalStatus.REQUEST_ACCEPT" />
          <el-option :label="yibaoWithdrawalStatusLabel[EYibaoWithdrawalStatus.SUCCESS]" :value="EYibaoWithdrawalStatus.SUCCESS" />
          <el-option :label="yibaoWithdrawalStatusLabel[EYibaoWithdrawalStatus.FAIL]" :value="EYibaoWithdrawalStatus.FAIL" />
          <el-option :label="yibaoWithdrawalStatusLabel[EYibaoWithdrawalStatus.REMITING]" :value="EYibaoWithdrawalStatus.REMITING" />
        </el-select>
      </search-col>

      <search-col>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
          format="YYYY-MM-DD"
          :default-time="defaultTimeFormat"
          style="width: calc(100% - 4px)"
          start-placeholder="交易开始时间"
          end-placeholder="交易完成时间"
          @keydown.enter="doSearch"
        />
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button class="mr-[4px]" type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <div>
      <el-button class="mb-[20px]" type="primary" @click="exportFile">
        <svg-icon icon="lucide:square-arrow-out-up-right" />导出
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :max-height="maxHeight" :data="tableData">
      <el-table-column align="left" prop="requestNo" label="易宝订单编号" show-overflow-tooltip :min-width="120" />
      <el-table-column align="left" prop="orderNo" label="平台订单编号" show-overflow-tooltip :min-width="120" />
      <el-table-column align="left" prop="accountName" label="收款方名称" show-overflow-tooltip :min-width="160" />
      <el-table-column align="left" prop="accountNo" label="收款方账号" show-overflow-tooltip :min-width="180" />
      <el-table-column align="right" prop="orderAmount" label="提现金额(元)" :width="110">
        <template #default="scope">
          {{ formatMoneyString(scope.row.orderAmount) || '-' }}
        </template>
      </el-table-column>
      <el-table-column align="center" prop="status" label="处理状态" :width="110">
        <template #default="scope">
          <custom-tag :label="yibaoWithdrawalStatusLabel[scope.row.status]" :type="yibaoWithdrawalStatusStyle[scope.row.status]" />
        </template>
      </el-table-column>
      <el-table-column align="left" prop="failReason" label="原因" show-overflow-tooltip :min-width="120">
        <template #default="scope">
          {{ scope.row.failReason||'-' }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="finishTime" label="交易完成时间" show-overflow-tooltip :width="170">
        <template #default="scope">
          {{ scope.row.finishTime||'-' }}
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-if="total > 0"
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :class="{ 'fix-fixed-footer': fixedFooter }"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>
  </content-container>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, CustomTag } from '@vue3/components';
import { useRoute } from 'vue-router';
import { downloadFile, getFomattedTimeRange, formatMoneyString } from '@core/tools';
import { EYibaoWithdrawalStatus, yibaoWithdrawalStatusLabel, yibaoWithdrawalStatusStyle } from '../../../../types/yibao-manage';
import { ContentContainer, SearchContainer, SearchCol, queryYibaoCashPageList, useCardTableHeightFix, exportYibaoCashPage } from '../../../..';

defineOptions({ name: 'Withdrawal' });
const route = useRoute();

const loading = ref(false);
const yibaoId = computed(() => route.query.yibaoId as string);
const tableData = ref([]);
const defaultPageSize = 12;
const total = ref(0);
const params = reactive({
  requestNo: '',
  orderNo: '',
  accountNo: '',
  status: '',
  page: 1,
  size: defaultPageSize,
});
const dateRange = ref([]);
const defaultTimeFormat = computed (() => getFomattedTimeRange());
const source = computed(() => route.query.from || 'company');
const { maxHeight, fixedFooter } = useCardTableHeightFix(total);

function resetForm() {
  dateRange.value = [];

  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = '';
    }
  }
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryYibaoCashPageList({
      ...params,
      id: Number(yibaoId.value),
      finishTimeStart: dateRange.value[0],
      finishTimeEnd: dateRange.value[1],
    }, source.value == 'company');

    tableData.value = data;
    total.value = all;

  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

async function exportFile() {
  try {
    const { buffer, fileName } = await exportYibaoCashPage({
      ...params,
      id: Number(yibaoId.value),
      finishTimeStart: dateRange.value[0],
      finishTimeEnd: dateRange.value[1],
    }, source.value == 'company');

    downloadFile(buffer, fileName, null);
    ElMessage.success('导出成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

fetchData();
</script>

<style lang="scss" scoped>
:deep(.number-range-input.sxf) {
  & > span {
    position: relative;
    left: -15px;
  }
}
</style>
